/**
 * Photo Rating Type Definitions (Frontend Layer)
 *
 * Feature: 649-critique-feedback-options
 * Types for star ratings in critique sessions.
 */

/**
 * Photo rating from GraphQL response
 */
export interface PhotoRating {
  id: string;
  photoId: string;
  sessionId: string;
  userId: string;
  rating: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Rating summary for a photo in a session
 * Used for photographer view (aggregate of all critic ratings)
 */
export interface RatingSummary {
  photoId: string;
  averageRating: number;
  voteCount: number;
}

/**
 * Available feedback types for critique sessions
 * Feature 651: Added 'n_comments' for aggregate N comments task
 */
export type FeedbackType = 'general' | 'rating' | 'n_comments';

/**
 * Configuration for N Comments feedback type
 * Feature: 651-n-comments-task
 */
export interface NCommentsConfig {
  /** Target number of distinct photos to comment on (1 to photoCount) */
  targetCount: number;
}

/**
 * Feedback options configuration for a session
 * Feature 651: Extended with nComments config
 */
export interface FeedbackOptions {
  enabled: FeedbackType[];
  /** Configuration for N Comments feedback (if enabled) */
  nComments?: NCommentsConfig;
}

/**
 * Check if general feedback (comments) is enabled
 */
export function hasGeneralFeedback(options: FeedbackOptions | undefined | null): boolean {
  if (!options?.enabled) return true; // Default to general enabled
  return options.enabled.includes('general');
}

/**
 * Check if rating feedback (stars) is enabled
 */
export function hasRatingFeedback(options: FeedbackOptions | undefined | null): boolean {
  if (!options?.enabled) return false; // Default to rating disabled
  return options.enabled.includes('rating');
}

/**
 * Check if N Comments feedback is enabled
 * Feature: 651-n-comments-task
 */
export function hasNCommentsFeedback(options: FeedbackOptions | undefined | null): boolean {
  if (!options?.enabled) return false;
  return options.enabled.includes('n_comments');
}

/**
 * Default feedback options for backward compatibility
 */
export const DEFAULT_FEEDBACK_OPTIONS: FeedbackOptions = {
  enabled: ['general']
};

/**
 * Default ratio for calculating N Comments target
 * E.g., 0.33 means default N = photoCount / 3
 * Feature: 651-n-comments-task
 */
export const DEFAULT_N_COMMENTS_RATIO = 1 / 3;

/**
 * Calculate the default N value for N Comments feedback
 * Based on DEFAULT_N_COMMENTS_RATIO of photos, rounded down, with minimum of 1
 * Feature: 651-n-comments-task
 *
 * @param photoCount - Number of photos in the collection
 * @returns Default N value (minimum 1)
 */
export function calculateDefaultNComments(photoCount: number): number {
  if (photoCount <= 0) {
    return 1; // Safe default
  }
  return Math.max(1, Math.floor(photoCount * DEFAULT_N_COMMENTS_RATIO));
}
